import React, { useState, useMemo, useCallback } from 'react';
import MaterialTable from 'material-table';
import { IconButton, Tooltip, withStyles, Typography } from '@material-ui/core';
import {
    BackButton,
    localization,
    hasAtLeastOnePermission,
    getDimensionsOfPagination,
    hasPermission
} from '../../sc-utilities';
import { Refresh, SyncAlt, NoteAdd, SupervisedUserCircle, CloudUpload, AllInbox, Add, Delete } from '@material-ui/icons';
import * as actions from './store/storageProductActions';
import * as action from '../../np-core/Auth/store/authActions';
import * as pcActions from '../PCProduct/store/pcProductActions';
import { connect } from 'react-redux';
import { CsvBuilder } from 'filefy';
import Toolbar from '../../sc-utilities/Toolbar'
import { MuiTableRows } from '../../../themes';
import { fetchBulkChangeProductsGroup } from "../../sc-group/Group/store/groupActions";
import ChangeGroupModal from "../../sc-users/User/ChangeGroupModal";
import { CreateShreddedProductDialog } from './CreateShreddedProductDialog';
import ProductDelete from '../ProductDelete';
import { snackActions } from '../../sc-utilities/SnackBar';

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});
let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const StorageProductList = props => {
    const { tableRef, classes, translations, permissions, saveColumnsFilter, activeCustomer, activeWarehouseID, tableColumns, onDownloadReport, fetchStorageProduct,
        fetchStorageProductCount, warehouseActive, setWarehouseActive, onBulkChangeProductGroup, groups, onSendExternalData, onSendErasureReports, productEditable } = props;

    const [changeGroupModalState, setChangeGroupModalState] = useState({ open: false, ProductIDs: [] });

    const [createShreddedProductDialogOpen, setCreateShreddedProductDialogOpen] = useState(false);

    const customExport = (columns, storageProductList) => {
        if (columns && columns[0].field === 'Action') {
            columns.shift()
        }

        const data = storageProductList.map(storageProduct => {
            let returnObj = [
                columns[0].render(storageProduct),
                columns[1].render(storageProduct).props.value,
                columns[3].render(storageProduct).props.value,
                columns[4].render(storageProduct).props.children.filter(n => {
                    let data
                    if (n.replace(/\s/g, '') !== '') {
                        data = n;
                    }
                    return data
                }).join(', '),
                columns[20].render(storageProduct).map(status => { return status.props.value }).join(', '),
                columns[6].render(storageProduct).props.children[0].props.children,
                columns[7].render(storageProduct).props.children
            ];
            return returnObj;
        });
        const builder = new CsvBuilder(`securaze_workplace_${Math.floor(Date.now() / 1000)}.csv`);
        builder
            .setDelimeter('|')
            .setColumns(columns.map(columnDef => columnDef.title))
            .addRows(data)
            .exportFile();
    };

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: hasAtLeastOnePermission(permissions, ["product_generate_report", "product_change_group"]),
        exportFileName: 'StorageProduct',
        filterCellStyle: { maxWidth: '150px' },
        search: false,
        minBodyHeight: window.innerHeight - 198 < 300 ? 300 : window.innerHeight - 198,
        maxBodyHeight: window.innerHeight - 198,
        pageSizeOptions: rowsPageSizeInit,
        exportCsv: customExport,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const [deleteModalState, setDeleteModalState] = useState({ open: false, productID: null, productIDs: [], forBulk: true });
    const [notEditableProducts, setNotEditableProducts] = useState([]);

    
    const handleBulkDelete = async products => {
        const ProductIDsForDelete = [];

        for(const product of products) {
          const isProductEditable = await productEditable({ ProductID: product.ProductID, ActiveCustomerID: activeCustomer });

          isProductEditable?.productEditable ? ProductIDsForDelete.push(product.ProductID) : setNotEditableProducts(prevState => {
              return [...prevState, product.SerialNumber]
            })

        };

        setDeleteModalState({ ...deleteModalState, open: true, productIDs: ProductIDsForDelete });
    };

    const handleDelete = async event => {
        if (!event || event.deleted) {
            setDeleteModalState({ open: false, productID: null, productIDs: [], forBulk: true });
            if(notEditableProducts && notEditableProducts.length){
                snackActions.snackBar({
                    message: `Products with serial numbers ${notEditableProducts.join(', ')} were not deleted due to "in progress" wipe status.`,
                    options: {
                        variant: 'warning'
                    }
                });
            setNotEditableProducts([]);
            }            
            tableRef.current && tableRef.current.onQueryChange();
            return;
        }
        const productIDs = event.currentTarget.value;

        setDeleteModalState({ ...deleteModalState, open: true, productIDs: productIDs });
    };

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = { ...q, WarehouseID: warehouseActive.name[0] ? warehouseActive.name[0].value : warehouseActive.data[0] ? warehouseActive.data[0].value : activeWarehouseID };

        if (query['q'].SecurazeSolution && query['q'].SecurazeSolution.length === 0) {
            query['q'].SecurazeSolution = null;
        }
        if (query['q'].WipeMethod && query['q'].WipeMethod.length === 0) {
            query['q'].WipeMethod = null;
        }

        let count = await fetchStorageProductCount(query);
        if (!query['page'] && !rowsPageSizeInit.includes(query['pageSize'])) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await fetchStorageProduct(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }


    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })

        objToSendColumn[`StorageProduct`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}> {translations["storage_product"] || 'Storage product'} ({warehouseActive.name.length > 0 ? warehouseActive.name[0].displayValue : ''})</Typography>
                <Tooltip title={translations["change_warehouse_id"] || "Change active warehouse"}>
                    <IconButton aria-label={translations["change_warehouse_id"] || "Change active warehouse"} component="div" onClick={() => setWarehouseActive({ ...warehouseActive, open: true })}>
                        <SyncAlt id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
                <Tooltip title={translations["create_shredded_storage_product"] || "Create shredded storage product"}>
                    <IconButton aria-label={translations["create_shredded_storage_product"] || "Create shredded storage product"} component="div" onClick={() => setCreateShreddedProductDialogOpen(true)}>
                        <Add />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const downloadErasureReport = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onDownloadReport({ StorageProductIDs: ids, ActiveCustomerID: activeCustomer, ErasureReportType: "pdf" })
    }, [activeCustomer, onDownloadReport, tableRef]);

    const sendExternalData = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onSendExternalData({ ProductIDs: ids, ActiveCustomerID: activeCustomer })
    }, [activeCustomer, onSendExternalData, tableRef]);

    const sendErasureReports = useCallback(async (ids) => {
        tableRef.current.onAllSelected(false);
        await onSendErasureReports({ ProductIDs: ids, ActiveCustomerID: activeCustomer })
    }, [activeCustomer, onSendErasureReports, tableRef]);

    // const downloadDiagnoseReport = useCallback(async (ids) => {
    //     tableRef.current.onAllSelected(false);
    //     await onDownloadReport({ StorageProductIDs: ids, ActiveCustomerID: activeCustomer, ErasureReportType: "diagnose-pdf" })
    // }, [activeCustomer, onDownloadReport, tableRef]);

    const handleBulkChangeUsersGroup = ProductIDs => {
        setChangeGroupModalState({ open: true, ProductIDs });
    };

    const handleChangeGroupCloseSafe = () => {
        setChangeGroupModalState({ open: false });
    };

    const handleChangeGroupCloseDanger = async (GroupID) => {
        await onBulkChangeProductGroup({ ProductIDs: changeGroupModalState.ProductIDs, ActiveCustomerID: activeCustomer, GroupID });
        setChangeGroupModalState({ open: false });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableActions = [
        {
            tooltip: translations['download_erasure_report'] || "Download erasure report",
            icon: <NoteAdd />,
            onClick: (rowData) => downloadErasureReport(rowData.map(storageProducts => storageProducts.ProductID))
        },
        {
            tooltip: translations['send_external_data'] || "Send external data",
            icon: <CloudUpload />,
            onClick: (rowData) => sendExternalData(rowData.map(pcProduct => pcProduct.ProductID))
        },
        {
            tooltip: translations['send_erasure_reports'] || "Send erasure reports",
            icon: <AllInbox />,
            onClick: (rowData) => sendErasureReports(rowData.map(pcProduct => pcProduct.ProductID))
        },
        // {
        //     tooltip: translations['download_diagnose_report'] || "Download diagnose report",
        //     icon: <NoteAdd />,
        //     onClick: (rowData) => downloadDiagnoseReport(rowData.map(pcProduct => pcProduct.ProductID))
        // },
        hasPermission(permissions, 'product_change_group') ? {
            tooltip: translations["change_group"] || "Change group",
            icon: <SupervisedUserCircle />,
            onClick: (rowData) => handleBulkChangeUsersGroup(rowData.map(product => product.ProductID))
        } : {},
        hasPermission(permissions, 'product_delete') ? {
            tooltip: translations['bulk_delete'] || 'Bulk delete',
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData)
        } : {}
    ]
    const toolbar = {
        Toolbar: props => (
            <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
        )
    }

    return (<>
        <MaterialTable
            tableRef={tableRef}
            title={tableTitle()}
            columns={columnObject}
            data={fetchTableData}
            options={tableOptions}
            components={toolbar}
            onChangeRowsPerPage={(size) => {
                pageSize = size
            }}
            localization={localization(translations)}
            onChangeColumnHidden={columnHidden}
            onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
        />

        {hasPermission(permissions, 'product_change_group') ? (
            <ChangeGroupModal
                groups={groups}
                entityName={translations['users'] || 'Users'}
                translations={translations}
                handleCloseDanger={handleChangeGroupCloseDanger}
                handleCloseSafe={handleChangeGroupCloseSafe}
                modalState={changeGroupModalState}
                permissions={permissions}
            />
        ) : null}

        {hasPermission(permissions, 'storage_product_create') ? (
            <CreateShreddedProductDialog 
                isOpen={createShreddedProductDialogOpen} 
                setIsOpen={setCreateShreddedProductDialogOpen}
                translations={translations}
                fetchStorageProduct={() => tableRef.current && tableRef.current.onQueryChange()}
            />
        ) : null},
        { hasPermission(permissions, 'product_delete') ? 
            <ProductDelete  entityName={translations['storage_product'] || 'Storage product'} translations={translations} activeCustomer={activeCustomer} handleDelete={handleDelete} deleteModalState={deleteModalState} deleteProduct={props.onDeleteProduct} bulkDeleteProducts={props.onBulkDeleteProducts} /> 
            : null}

    </>)
}

const mapDispatchToProps = dispatch => {
    return {
        fetchStorageProductCount: filter => dispatch(actions.fetchStorageProductDataCount(filter)),
        fetchStorageProduct: filter => dispatch(actions.fetchStorageProductData(filter)),
        onDownloadReport: params => dispatch(actions.downloadReport(params)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkChangeProductGroup: data => dispatch(fetchBulkChangeProductsGroup(data)),
        onSendExternalData: params => dispatch(pcActions.sendExternalData(params)),
        onSendErasureReports: params => dispatch(pcActions.sendErasureReports(params)),
        productEditable: data => dispatch(pcActions.productEditable(data)),
        onBulkDeleteProducts: data => dispatch(actions.bulkDeleteStorageProducts(data)),
    };
};

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        activeWarehouseID: state.core.login.user.ActiveWarehouseID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        storageProducts: state.product.storageProducts,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(StorageProductList));